import React, { useEffect, useRef } from 'react';
import { Html } from '@react-three/drei';

interface Message {
    id: number;
    role: 'user' | 'assistant';
    text: string;
}

interface HolographicChatProps {
    messages: Message[];
}

export function HolographicChat({ messages }: HolographicChatProps) {
    const scrollRef = useRef<HTMLDivElement>(null);

    // Auto-scroll to bottom
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);

    const styles = {
        container: {
            width: '400px',
            height: '600px',
            background: 'rgba(0, 10, 20, 0.4)', // Very transparent dark blue
            backdropFilter: 'blur(8px)',        // Frosted glass effect
            border: '1px solid rgba(0, 243, 255, 0.3)',
            borderRadius: '15px',
            padding: '20px',
            fontFamily: 'Courier New',
            display: 'flex',
            flexDirection: 'column' as const,
            boxShadow: '0 0 30px rgba(0, 243, 255, 0.1)',
            transform: 'scale(1)',
            overflow: 'hidden'
        },
        header: {
            borderBottom: '1px solid rgba(0, 243, 255, 0.3)',
            paddingBottom: '10px',
            marginBottom: '10px',
            color: '#00f3ff',
            fontSize: '14px',
            fontWeight: 'bold',
            letterSpacing: '2px',
            textShadow: '0 0 5px #00f3ff'
        },
        log: {
            flex: 1,
            overflowY: 'auto' as const,
            display: 'flex',
            flexDirection: 'column' as const,
            gap: '15px',
            paddingRight: '10px'
        },
        message: (role: string) => ({
            alignSelf: role === 'user' ? 'flex-end' : 'flex-start',
            maxWidth: '85%',
            padding: '10px 15px',
            borderRadius: '10px',
            background: role === 'user' ? 'rgba(0, 243, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)',
            borderLeft: role === 'assistant' ? '2px solid #00f3ff' : 'none',
            borderRight: role === 'user' ? '2px solid #00f3ff' : 'none',
            color: role === 'user' ? '#fff' : '#aaa',
            fontSize: '14px',
            lineHeight: '1.4'
        }),
        input: {
            marginTop: '15px',
            background: 'rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(0, 243, 255, 0.3)',
            borderRadius: '20px',
            padding: '10px 15px',
            color: '#fff',
            fontSize: '14px',
            outline: 'none',
            width: '100%'
        }
    };

    return (
        // Position: Left side of the user, slightly rotated
        <group position={[-3.5, 1.5, -2]} rotation={[0, 0.5, 0]}>
            <Html transform occlude>
                <div style={styles.container}>
                    <div style={styles.header}>COMM_LINK // SECURE</div>

                    <div ref={scrollRef} style={styles.log}>
                        {messages.length === 0 && (
                            <div style={{ color: '#555', textAlign: 'center', marginTop: '50%' }}>
                                Waiting for signal...
                            </div>
                        )}
                        {messages.map((msg) => (
                            <div key={msg.id} style={styles.message(msg.role)}>
                                <div style={{ fontSize: '10px', opacity: 0.7, marginBottom: '4px' }}>
                                    {msg.role === 'user' ? 'COMMANDER' : 'ISSIE'}
                                </div>
                                {msg.text}
                            </div>
                        ))}
                    </div>

                    {/* Faux Input (Voice Indicator) */}
                    <div style={{ marginTop: '10px', fontSize: '10px', color: '#00f3ff', opacity: 0.7 }}>
                        VOICE CHANNEL ACTIVE <span style={{ animation: 'blink 1s infinite' }}>●</span>
                    </div>
                </div>
            </Html>
        </group>
    );
}
